import { useState } from "react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../lib/motion";
import { OFFICES, CONTACT } from "../lib/data";
import { toast } from "sonner";
import { postContact } from "../lib/api";
import { Mail, Phone, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const INTERESTS = ["AI Transformation", "AI Solutions & Automations", "Cybersecurity", "Cloud Engineering", "Data Engineering & AI Analytics", "SAP Implementation", "Extended PODs / GCC", "Market Partnership", "Other"];

const empty = { name: "", email: "", company: "", phone: "", interest: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await postContact(form);
      toast.success("Thank you. Our team will be in touch within one business day.");
      setForm(empty);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        testid="contact-hero"
        overline="Contact"
        title="Let's connect."
        subtitle="Whether you're evaluating a technology partner or exploring a market partnership, we'd like to hear from you. Every first engagement begins with a 45-minute scoping conversation."
      />

      <section className="bg-background py-24">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="overline text-gradient">Get in touch</p>
            <div className="mt-8 space-y-6">
              <a href={`mailto:${CONTACT.email}`} className="group flex items-center gap-4">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-secondary text-navy dark:text-white"><Mail className="h-5 w-5" /></span>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium transition-colors group-hover:text-accent">{CONTACT.email}</p>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-secondary text-navy dark:text-white"><Phone className="h-5 w-5" /></span>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{CONTACT.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-secondary text-navy dark:text-white"><MapPin className="h-5 w-5" /></span>
                <div>
                  <p className="text-sm text-muted-foreground">Locations</p>
                  <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-1">
                    {OFFICES.map((o) => (
                      <p key={o.city} className="text-sm font-medium">{o.city}, {o.country}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-2xl border border-border bg-background p-8 shadow-[0_8px_32px_rgba(27,26,36,0.05)] md:p-10"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">Full name *</label>
                  <Input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="h-12" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Work email *</label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className="h-12" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Company</label>
                  <Input data-testid="contact-company" value={form.company} onChange={set("company")} placeholder="Company Inc." className="h-12" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Phone</label>
                  <Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" className="h-12" />
                </div>
              </div>
              <div className="mt-5">
                <label className="mb-2 block text-sm font-medium">Area of interest</label>
                <Select value={form.interest} onValueChange={set("interest")}>
                  <SelectTrigger data-testid="contact-interest" className="h-12">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERESTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-5">
                <label className="mb-2 block text-sm font-medium">How can we help? *</label>
                <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="Tell us about your goals..." rows={5} />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-accent disabled:opacity-70 dark:bg-white dark:text-navy dark:hover:bg-accent dark:hover:text-white sm:w-auto"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Schedule Consultation <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
